"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState, useTransition } from "react";
import type { MouseEvent, ReactNode } from "react";
import { Check, ChevronLeft, ChevronRight, Clock3, MessageCircle, Plus, X } from "lucide-react";
import { ForumContextBackLink } from "@/components/forum/forum-context-back-link";
import { createForumThreadAction, inactivateForumThread, removeForumThread } from "@/lib/forum-actions";
import { cn, formatDate } from "@/lib/utils";

type LeagueFilter = {
  id: string;
  name: string;
  slug: string;
};

type MatchThreadItem = {
  id: string;
  slug: string;
  title: string;
  score: number;
  commentCount: number;
  isLive: boolean;
  leagueName: string;
  leagueSlug: string | null;
  kickoff: string | null;
  status: string | null;
  homeName: string;
  awayName: string;
  isWorldCup: boolean;
};

type GeneralThreadItem = {
  id: string;
  slug: string;
  title: string;
  score: number;
  commentCount: number;
  forumName: string;
  lastActivityAt: string;
};

type CreateForumTarget = {
  id: string;
  name: string;
  slug: string;
} | null;

type ThreadModerationMenuState = {
  threadId: string;
  x: number;
  y: number;
} | null;

const liveStatuses = new Set(["LIVE", "IN_PLAY", "PAUSED"]);
const finishedStatuses = new Set(["FINISHED", "FT", "AFTER_EXTRA_TIME", "PENALTY_SHOOTOUT", "AWARDED", "CANCELLED"]);
const MATCH_PAGE_SIZE = 7;

function timeLabel(item: MatchThreadItem) {
  if (!item.kickoff) return item.status ?? "Idopont kesobb";
  if (liveStatuses.has(item.status ?? "") || item.isLive) return item.status ?? "LIVE";
  return formatDate(item.kickoff);
}

function StatusMark({ item }: { item: MatchThreadItem }) {
  if (liveStatuses.has(item.status ?? "") || item.isLive) {
    return (
      <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center">
        <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-[#A4243B]" />
      </span>
    );
  }
  if (finishedStatuses.has(item.status ?? "")) {
    return <Check size={16} className="mt-0.5 shrink-0 text-[#273E47]" />;
  }
  return <Clock3 size={16} className="mt-0.5 shrink-0 text-[#273E47]/60" />;
}

function useSidePanelDismiss(
  rootRef: React.RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
) {
  const handlePointerDown = useEffectEvent((event: PointerEvent) => {
    if (!rootRef.current?.contains(event.target as Node)) {
      onClose();
    }
  });
  const handleKeyDown = useEffectEvent((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      onClose();
    }
  });

  useEffect(() => {
    if (!open) return;
    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);
}

function ThreadCreatePanel({
  forum,
  isLoggedIn,
  selectedMatch,
  open,
  onOpenChange,
}: {
  forum: CreateForumTarget;
  isLoggedIn: boolean;
  selectedMatch: MatchThreadItem | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<"DISCUSSION" | "MATCH">("DISCUSSION");
  const [error, setError] = useState<string | null>(null);
  const [pending, startCreateTransition] = useTransition();
  useSidePanelDismiss(rootRef, open, () => onOpenChange(false));

  const body = title.trim() ? `Beszélgessünk erről: ${title.trim()}` : "";

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        aria-label="Új thread"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#D8C99B]/70 text-[#273E47] transition hover:bg-[#D8C99B]/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
      >
        <Plus size={16} aria-hidden="true" />
      </button>

      {open ? (
        <div className="absolute right-0 top-full z-30 mt-2 w-[min(21rem,calc(100vw-2rem))] rounded-xl border border-[#D8C99B]/70 bg-[#FCFBF8] p-3 text-[#273E47] shadow-[0_14px_34px_rgba(39,62,71,0.12)] md:left-full md:right-auto md:top-0 md:ml-3 md:mt-0">
          <div className="absolute -top-1.5 right-2.5 h-3 w-3 rotate-45 border-l border-t border-[#D8C99B]/70 bg-[#FCFBF8] md:-left-1.5 md:right-auto md:top-2.5 md:border-b md:border-l md:border-r-0 md:border-t-0" aria-hidden="true" />
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-black text-[#273E47]">Új thread</p>
              <p className="mt-0.5 text-[11px] font-semibold text-[#273E47]/60">
                {selectedMatch ? `${selectedMatch.homeName} vs ${selectedMatch.awayName}` : forum?.name ?? "Forum"}
              </p>
            </div>
            <button
              type="button"
              aria-label="Panel bezárása"
              onClick={() => onOpenChange(false)}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[#273E47]/55 transition hover:bg-[#D8C99B]/25 hover:text-[#273E47]"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>

          {isLoggedIn && forum ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                setError(null);
                startCreateTransition(async () => {
                  try {
                    const result = await createForumThreadAction(formData);
                    if (result && !result.ok) setError(result.message);
                  } catch (submitError) {
                    console.error("[forum-ui] thread create failed", submitError);
                    setError("A thread létrehozása most nem sikerült. Próbáld újra később.");
                  }
                });
              }}
              className="mt-3 grid gap-3"
            >
              <input type="hidden" name="forumId" value={forum.id} />
              <input type="hidden" name="flair" value={type === "MATCH" ? "MATCH" : "GENERAL"} />
              <input type="hidden" name="type" value={type} />
              <input type="hidden" name="body" value={body} />

              <div className="grid grid-cols-2 rounded-full border border-[#D8C99B]/70 bg-[#D8C99B]/18 p-0.5">
                <button
                  type="button"
                  onClick={() => setType("DISCUSSION")}
                  className={cn(
                    "rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] transition",
                    type === "DISCUSSION" ? "bg-[#A4243B] text-white" : "text-[#273E47]/70 hover:bg-[#FCFBF8] hover:text-[#273E47]",
                  )}
                >
                  Általános
                </button>
                <button
                  type="button"
                  onClick={() => setType("MATCH")}
                  className={cn(
                    "rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] transition",
                    type === "MATCH" ? "bg-[#A4243B] text-white" : "text-[#273E47]/70 hover:bg-[#FCFBF8] hover:text-[#273E47]",
                  )}
                >
                  Meccs
                </button>
              </div>

              <input
                name="title"
                value={title}
                onChange={(event) => setTitle(event.currentTarget.value)}
                placeholder="Miről beszélgessünk?"
                minLength={6}
                required
                className="h-10 rounded-lg border border-[#D8C99B]/70 bg-white/65 px-3 text-sm font-semibold text-[#273E47] outline-none transition placeholder:text-[#273E47]/38 focus:border-[#A4243B] focus:ring-2 focus:ring-[#A4243B]/15"
              />
              {error ? <p className="rounded-lg bg-[#A4243B]/10 px-3 py-2 text-xs font-semibold text-[#A4243B]" role="alert">{error}</p> : null}

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="rounded-full border border-[#D8C99B]/70 px-3 py-2 text-[11px] font-black uppercase tracking-[0.12em] text-[#273E47]/70 transition hover:bg-[#D8C99B]/18 hover:text-[#273E47]"
                >
                  Mégse
                </button>
                <button disabled={pending} className="rounded-full bg-[#A4243B] px-4 py-2 text-[11px] font-black uppercase tracking-[0.12em] text-white transition hover:bg-[#273E47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20 disabled:cursor-wait disabled:opacity-60">
                  {pending ? "Küldés..." : "Thread létrehozása"}
                </button>
              </div>
            </form>
          ) : (
            <div className="mt-3 rounded-lg border border-dashed border-[#A4243B]/30 bg-white/45 p-3">
              <p className="text-sm font-semibold leading-6 text-[#273E47]/75">
                Jelentkezz be, ha új fórum threadet indítanál.
              </p>
              <Link
                href="/login"
                onClick={() => onOpenChange(false)}
                className="mt-3 inline-flex rounded-full bg-[#A4243B] px-4 py-2 text-[11px] font-black uppercase tracking-[0.12em] text-white transition hover:bg-[#273E47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
              >
                Belépés
              </Link>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function HorizontalThreadNav({
  children,
  className,
  ariaLabel,
}: {
  children: ReactNode;
  className?: string;
  ariaLabel: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    setHasOverflow(maxScrollLeft > 1);
    setCanScrollLeft(element.scrollLeft > 1);
    setCanScrollRight(element.scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const element = scrollRef.current;
    if (!element) return;

    element.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });

    element.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      element.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [children, updateScrollState]);

  const scrollByPage = (direction: -1 | 1) => {
    const element = scrollRef.current;
    if (!element) return;
    element.scrollBy({ left: direction * Math.max(220, element.clientWidth * 0.75), behavior: "smooth" });
  };

  return (
    <div className="group/thread-nav relative min-w-0">
      {hasOverflow ? (
        <>
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-[#FCFBF8] to-transparent transition-opacity",
              canScrollLeft ? "opacity-100" : "opacity-0",
            )}
          />
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-[#FCFBF8] to-transparent transition-opacity",
              canScrollRight ? "opacity-100" : "opacity-0",
            )}
          />
          <button
            type="button"
            aria-label="Balra"
            disabled={!canScrollLeft}
            onClick={() => scrollByPage(-1)}
            className="absolute left-1 top-1/2 z-20 hidden h-7 w-7 -translate-y-1/2 place-items-center rounded-full border border-black/5 bg-[#F7F1EC]/90 text-[#273E47]/75 opacity-0 shadow-sm transition hover:text-[#273E47] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20 disabled:pointer-events-none disabled:opacity-0 group-hover/thread-nav:opacity-100 group-focus-within/thread-nav:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:grid"
          >
            <ChevronLeft size={15} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Jobbra"
            disabled={!canScrollRight}
            onClick={() => scrollByPage(1)}
            className="absolute right-1 top-1/2 z-20 hidden h-7 w-7 -translate-y-1/2 place-items-center rounded-full border border-black/5 bg-[#F7F1EC]/90 text-[#273E47]/75 opacity-0 shadow-sm transition hover:text-[#273E47] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20 disabled:pointer-events-none disabled:opacity-0 group-hover/thread-nav:opacity-100 group-focus-within/thread-nav:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:grid"
          >
            <ChevronRight size={15} aria-hidden="true" />
          </button>
        </>
      ) : null}
      <div
        ref={scrollRef}
        className={cn(
          "min-w-0 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className,
        )}
      >
        <div className="flex w-max min-w-full flex-nowrap items-center gap-2 whitespace-nowrap" aria-label={ariaLabel}>
          {children}
        </div>
      </div>
    </div>
  );
}

export function ForumSidebar({
  leagues,
  matchThreads,
  generalThreads,
  worldCupGeneralThreads,
  createForum,
  isLoggedIn,
  isAdmin,
}: {
  leagues: LeagueFilter[];
  matchThreads: MatchThreadItem[];
  generalThreads: GeneralThreadItem[];
  worldCupGeneralThreads: GeneralThreadItem[];
  createForum: CreateForumTarget;
  isLoggedIn: boolean;
  isAdmin: boolean;
}) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const activeLeague = searchParams.get("league") ?? "all";
  const selectedSlug = pathname.startsWith("/forum/") ? pathname.split("/").filter(Boolean)[1] : null;
  const normalMatchThreads = useMemo(() => matchThreads.filter((item) => !item.isWorldCup), [matchThreads]);
  const worldCupMatchThreads = useMemo(() => matchThreads.filter((item) => item.isWorldCup), [matchThreads]);
  const visibleMatches = useMemo(
    () => (activeLeague === "all" ? normalMatchThreads : normalMatchThreads.filter((item) => item.leagueSlug === activeLeague)),
    [activeLeague, normalMatchThreads],
  );
  const selectedGeneral = generalThreads.some((thread) => thread.slug === selectedSlug);
  const selectedWorldCupGeneral = worldCupGeneralThreads.some((thread) => thread.slug === selectedSlug);
  const selectedMatchIndex = visibleMatches.findIndex((thread) => thread.slug === selectedSlug);
  const selectedWorldCupMatchIndex = worldCupMatchThreads.findIndex((thread) => thread.slug === selectedSlug);
  const [activeTab, setActiveTab] = useState<"matches" | "worldCup" | "general">(
    selectedWorldCupGeneral || selectedWorldCupMatchIndex >= 0 ? "worldCup" : selectedGeneral ? "general" : "matches",
  );
  const [matchPage, setMatchPage] = useState(0);
  const [worldCupPage, setWorldCupPage] = useState(0);
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  const [moderationMenu, setModerationMenu] = useState<ThreadModerationMenuState>(null);
  const [loadingAction, setLoadingAction] = useState<"inactive" | "delete" | null>(null);
  const [isModerationPending, startModerationTransition] = useTransition();
  const moderationMenuRef = useRef<HTMLDivElement>(null);
  const pageCount = Math.max(1, Math.ceil(visibleMatches.length / MATCH_PAGE_SIZE));
  const worldCupPageCount = Math.max(1, Math.ceil(worldCupMatchThreads.length / MATCH_PAGE_SIZE));
  const pagedMatches = useMemo(
    () => visibleMatches.slice(matchPage * MATCH_PAGE_SIZE, matchPage * MATCH_PAGE_SIZE + MATCH_PAGE_SIZE),
    [matchPage, visibleMatches],
  );
  const pagedWorldCupMatches = useMemo(
    () => worldCupMatchThreads.slice(worldCupPage * MATCH_PAGE_SIZE, worldCupPage * MATCH_PAGE_SIZE + MATCH_PAGE_SIZE),
    [worldCupPage, worldCupMatchThreads],
  );

  useEffect(() => {
    setMatchPage(0);
  }, [activeLeague]);

  useEffect(() => {
    if (selectedGeneral) setActiveTab("general");
  }, [selectedGeneral]);

  useEffect(() => {
    if (selectedWorldCupGeneral) setActiveTab("worldCup");
  }, [selectedWorldCupGeneral]);

  useEffect(() => {
    if (selectedMatchIndex >= 0) {
      setActiveTab("matches");
      setMatchPage(Math.floor(selectedMatchIndex / MATCH_PAGE_SIZE));
    }
  }, [selectedMatchIndex]);

  useEffect(() => {
    if (selectedWorldCupMatchIndex >= 0) {
      setActiveTab("worldCup");
      setWorldCupPage(Math.floor(selectedWorldCupMatchIndex / MATCH_PAGE_SIZE));
    }
  }, [selectedWorldCupMatchIndex]);

  useEffect(() => {
    if (matchPage > pageCount - 1) setMatchPage(pageCount - 1);
  }, [matchPage, pageCount]);

  useEffect(() => {
    if (worldCupPage > worldCupPageCount - 1) setWorldCupPage(worldCupPageCount - 1);
  }, [worldCupPage, worldCupPageCount]);

  const queryForLeague = (slug: string) => (slug === "all" ? {} : { league: slug });
  const threadHref = (slug: string) => ({
    pathname: `/forum/${slug}`,
    query: activeLeague === "all" ? {} : { league: activeLeague },
  });
  const filterLabelClassName = "text-[10px] font-black uppercase tracking-[0.16em] text-[#273E47]/48";
  const filterPillClassName =
    "inline-flex h-7 shrink-0 items-center rounded-full px-3 text-xs font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20";
  const inactiveFilterPillClassName = "bg-[#D8C99B]/24 text-[#273E47]/62 hover:bg-[#D8C99B]/38 hover:text-[#273E47]";
  const activeTypePillClassName = "bg-[#273E47] text-white shadow-[0_4px_12px_rgba(39,62,71,0.14)]";
  const activeWorldCupPillClassName = "bg-[#A87520] text-white shadow-[0_4px_12px_rgba(168,117,32,0.18)]";
  const activeLeaguePillClassName = "bg-[#A4243B] text-white shadow-[0_4px_12px_rgba(164,36,59,0.14)]";
  const selectedMatch = matchThreads.find((thread) => thread.slug === selectedSlug) ?? null;
  const closeModerationMenu = useCallback(() => {
    setModerationMenu(null);
    setLoadingAction(null);
  }, []);
  const openModerationMenu = useCallback(
    (event: MouseEvent, threadId: string) => {
      if (!isAdmin) return;
      event.preventDefault();
      setModerationMenu({ threadId, x: event.clientX, y: event.clientY });
    },
    [isAdmin],
  );
  const runModerationAction = useCallback(
    (action: "inactive" | "delete") => {
      const threadId = moderationMenu?.threadId;
      if (!threadId || isModerationPending) return;
      if (action === "delete" && !window.confirm("Biztosan törlöd ezt a threadet?")) return;

      setLoadingAction(action);
      startModerationTransition(async () => {
        try {
          if (action === "inactive") {
            await inactivateForumThread(threadId);
          } else {
            await removeForumThread(threadId);
          }
          closeModerationMenu();
          router.refresh();
        } catch (error) {
          setLoadingAction(null);
          window.alert(error instanceof Error ? error.message : "A moderációs művelet sikertelen.");
        }
      });
    },
    [closeModerationMenu, isModerationPending, moderationMenu?.threadId, router],
  );

  useEffect(() => {
    closeModerationMenu();
  }, [pathname, activeLeague, activeTab, closeModerationMenu]);

  useEffect(() => {
    if (!moderationMenu) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!moderationMenuRef.current?.contains(event.target as Node)) {
        closeModerationMenu();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeModerationMenu();
      }
    };
    const handleScroll = () => closeModerationMenu();

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [closeModerationMenu, moderationMenu]);

  return (
    <div className="flex min-h-full flex-col">
      {isAdmin && moderationMenu ? (
        <div
          ref={moderationMenuRef}
          className="fixed z-50 min-w-44 overflow-hidden rounded-lg border border-[#273E47]/12 bg-[#273E47] py-1 text-sm font-bold text-white shadow-[0_16px_36px_rgba(39,62,71,0.22)]"
          style={{ left: moderationMenu.x, top: moderationMenu.y }}
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            disabled={isModerationPending}
            onClick={() => runModerationAction("inactive")}
            className="flex w-full items-center px-3 py-2 text-left transition hover:bg-white/10 disabled:cursor-wait disabled:opacity-60"
          >
            {loadingAction === "inactive" ? "Folyamatban..." : "Inaktívvá tétel"}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={isModerationPending}
            onClick={() => runModerationAction("delete")}
            className="flex w-full items-center px-3 py-2 text-left text-[#F7D6DC] transition hover:bg-[#A4243B] hover:text-white disabled:cursor-wait disabled:opacity-60"
          >
            {loadingAction === "delete" ? "Folyamatban..." : "Törlés"}
          </button>
        </div>
      ) : null}
      <header className="sticky top-0 z-10 border-b border-[#D8C99B]/60 bg-[#FCFBF8]/95 px-4 py-3 backdrop-blur sm:py-4">
        <ForumContextBackLink className="mb-2" />
        <div className="flex items-center justify-between gap-3">
          <h1 className="inline-flex min-w-0 items-center gap-2 text-base font-black text-[#273E47]">
            <MessageCircle size={18} className="shrink-0" />
            <span className="truncate">Meccs kibeszélők</span>
          </h1>
          {isAdmin ? (
            <ThreadCreatePanel
              forum={createForum}
              isLoggedIn={isLoggedIn}
              selectedMatch={selectedMatch}
              open={createPanelOpen}
              onOpenChange={setCreatePanelOpen}
            />
          ) : null}
        </div>
        <div className="mt-3 grid gap-3 rounded-lg bg-[#FCFBF8] p-3 shadow-[inset_0_0_0_1px_rgba(216,201,155,0.55)]">
          <div className="grid gap-1.5">
            <p className={filterLabelClassName}>Típus</p>
            <HorizontalThreadNav ariaLabel="Thread típusok">
              <button
                type="button"
                onClick={() => setActiveTab("worldCup")}
                data-active={activeTab === "worldCup"}
                className={cn(filterPillClassName, activeTab === "worldCup" ? activeWorldCupPillClassName : inactiveFilterPillClassName)}
              >
                VB2026
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("matches")}
                data-active={activeTab === "matches"}
                className={cn(filterPillClassName, activeTab === "matches" ? activeTypePillClassName : inactiveFilterPillClassName)}
              >
                Meccs kibeszélők
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("general")}
                data-active={activeTab === "general"}
                className={cn(filterPillClassName, activeTab === "general" ? activeTypePillClassName : inactiveFilterPillClassName)}
              >
                Általános
              </button>
            </HorizontalThreadNav>
          </div>
          {activeTab === "matches" ? (
            <div className="grid gap-1.5">
              <p className={filterLabelClassName}>Liga</p>
              <HorizontalThreadNav ariaLabel="Ligák">
                <Link
                  href={{ pathname: "/forum", query: queryForLeague("all") }}
                  data-active={activeLeague === "all"}
                  className={cn(filterPillClassName, activeLeague === "all" ? activeLeaguePillClassName : inactiveFilterPillClassName)}
                >
                  Összes
                </Link>
                {leagues.map((league) => (
                  <Link
                    key={league.id}
                    href={{ pathname: "/forum", query: queryForLeague(league.slug) }}
                    data-active={activeLeague === league.slug}
                    className={cn(filterPillClassName, activeLeague === league.slug ? activeLeaguePillClassName : inactiveFilterPillClassName)}
                  >
                    {league.name}
                  </Link>
                ))}
              </HorizontalThreadNav>
            </div>
          ) : null}
        </div>
      </header>

      {activeTab === "matches" ? (
        <>
          <div className="divide-y divide-[#D8C99B]/60">
            {visibleMatches.length ? (
              pagedMatches.map((thread) => {
            const selected = selectedSlug === thread.slug;
            return (
              <Link
                key={thread.id}
                href={{ pathname: `/forum/${thread.slug}` }}
                onContextMenu={(event) => openModerationMenu(event, thread.id)}
                className={cn(
                  "group flex gap-3 border-l-2 border-transparent px-4 py-3 transition hover:bg-[#D8C99B]/25",
                  selected ? "border-[#A4243B] bg-[#D8C99B]/18" : "",
                  liveStatuses.has(thread.status ?? "") || thread.isLive ? "data-[live=true]:border-[#A4243B]" : "",
                )}
                data-live={liveStatuses.has(thread.status ?? "") || thread.isLive}
              >
                <StatusMark item={thread} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-[#273E47]">
                    {thread.homeName} vs {thread.awayName}
                  </p>
                  <p className="mt-1 truncate text-xs text-[#273E47]/65">
                    {thread.leagueName} · {timeLabel(thread)}
                  </p>
                  <p className="mt-1 inline-flex items-center gap-4 text-xs text-[#273E47]/65">
                    <span className="inline-flex items-center gap-1">
                      <MessageCircle size={12} /> {thread.commentCount}
                    </span>
                  </p>
                </div>
              </Link>
            );
          })
            ) : (
              <div className="px-4 py-8 text-sm text-[#273E47]/65">Nincs meccs thread ebben a ligában.</div>
            )}
          </div>

          {visibleMatches.length > MATCH_PAGE_SIZE ? (
            <div className="flex items-center justify-between border-t border-[#D8C99B]/60 px-4 py-3">
              <button
                type="button"
                aria-label="Előző meccs threadek"
                disabled={matchPage === 0}
                onClick={() => setMatchPage((page) => Math.max(0, page - 1))}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/5 bg-[#F7F1EC]/90 text-[#273E47]/75 shadow-sm transition hover:text-[#273E47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20 disabled:pointer-events-none disabled:opacity-35"
              >
                <ChevronLeft size={15} />
              </button>
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-[#273E47]/50">
                {matchPage + 1}/{pageCount}
              </span>
              <button
                type="button"
                aria-label="Következő meccs threadek"
                disabled={matchPage >= pageCount - 1}
                onClick={() => setMatchPage((page) => Math.min(pageCount - 1, page + 1))}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/5 bg-[#F7F1EC]/90 text-[#273E47]/75 shadow-sm transition hover:text-[#273E47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20 disabled:pointer-events-none disabled:opacity-35"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          ) : null}
        </>
      ) : activeTab === "worldCup" ? (
        <>
          <div className="divide-y divide-[#D8C99B]/60">
            {worldCupMatchThreads.length ? (
              pagedWorldCupMatches.map((thread) => {
                const selected = selectedSlug === thread.slug;
                return (
                  <Link
                    key={thread.id}
                    href={{ pathname: `/forum/${thread.slug}` }}
                    onContextMenu={(event) => openModerationMenu(event, thread.id)}
                    className={cn(
                      "group flex gap-3 border-l-2 border-transparent px-4 py-3 transition hover:bg-[#D9A441]/14",
                      selected ? "border-[#D9A441] bg-[#D9A441]/16" : "",
                      liveStatuses.has(thread.status ?? "") || thread.isLive ? "data-[live=true]:border-[#A4243B]" : "",
                    )}
                    data-live={liveStatuses.has(thread.status ?? "") || thread.isLive}
                  >
                    <StatusMark item={thread} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-[#273E47]">
                        {thread.homeName} vs {thread.awayName}
                      </p>
                      <p className="mt-1 truncate text-xs text-[#273E47]/65">
                        {thread.leagueName} · {timeLabel(thread)}
                      </p>
                      <p className="mt-1 inline-flex items-center gap-4 text-xs text-[#273E47]/65">
                        <span className="inline-flex items-center gap-1">
                          <MessageCircle size={12} /> {thread.commentCount}
                        </span>
                      </p>
                    </div>
                  </Link>
                );
              })
            ) : (
              <div className="px-4 py-8 text-sm text-[#273E47]/65">Nincs VB2026 meccs thread.</div>
            )}
          </div>

          {worldCupMatchThreads.length > MATCH_PAGE_SIZE ? (
            <div className="flex items-center justify-between border-t border-[#D8C99B]/60 px-4 py-3">
              <button
                type="button"
                aria-label="Előző VB2026 threadek"
                disabled={worldCupPage === 0}
                onClick={() => setWorldCupPage((page) => Math.max(0, page - 1))}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/5 bg-[#F7F1EC]/90 text-[#273E47]/75 shadow-sm transition hover:text-[#273E47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D8973C]/25 disabled:pointer-events-none disabled:opacity-35"
              >
                <ChevronLeft size={15} />
              </button>
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-[#273E47]/50">
                {worldCupPage + 1}/{worldCupPageCount}
              </span>
              <button
                type="button"
                aria-label="Következő VB2026 threadek"
                disabled={worldCupPage >= worldCupPageCount - 1}
                onClick={() => setWorldCupPage((page) => Math.min(worldCupPageCount - 1, page + 1))}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/5 bg-[#F7F1EC]/90 text-[#273E47]/75 shadow-sm transition hover:text-[#273E47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D8973C]/25 disabled:pointer-events-none disabled:opacity-35"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          ) : null}

          {worldCupGeneralThreads.length ? (
            <section className="border-t border-[#D8C99B]/60">
              <p className="px-4 pt-4 text-[11px] font-black uppercase tracking-[0.16em] text-[#D9A441]">VB2026 beszélgetések</p>
              <div className="divide-y divide-[#D8C99B]/60">
                {worldCupGeneralThreads.map((thread) => (
                  <Link
                    key={thread.id}
                    href={threadHref(thread.slug)}
                    onContextMenu={(event) => openModerationMenu(event, thread.id)}
                    className={cn(
                      "block border-l-2 border-transparent px-4 py-3 transition hover:bg-[#D9A441]/14",
                      selectedSlug === thread.slug ? "border-[#D9A441] bg-[#D9A441]/16" : "",
                    )}
                  >
                    <p className="truncate text-sm font-bold text-[#273E47]">{thread.title}</p>
                    <p className="mt-1 truncate text-xs text-[#273E47]/65">
                      {thread.forumName} · {formatDate(thread.lastActivityAt)}
                    </p>
                    <p className="mt-1 inline-flex items-center gap-4 text-xs text-[#273E47]/65">
                      <span className="inline-flex items-center gap-1">
                        <MessageCircle size={12} /> {thread.commentCount}
                      </span>
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : generalThreads.length ? (
        <section>
          <div className="divide-y divide-[#D8C99B]/60">
            {generalThreads.map((thread) => (
              <Link
                key={thread.id}
                href={threadHref(thread.slug)}
                onContextMenu={(event) => openModerationMenu(event, thread.id)}
                className={cn(
                  "block border-l-2 border-transparent px-4 py-3 transition hover:bg-[#D8C99B]/25",
                  selectedSlug === thread.slug ? "border-[#A4243B] bg-[#D8C99B]/18" : "",
                )}
              >
                <p className="truncate text-sm font-bold text-[#273E47]">{thread.title}</p>
                <p className="mt-1 truncate text-xs text-[#273E47]/65">
                  {thread.forumName} · {formatDate(thread.lastActivityAt)}
                </p>
                <p className="mt-1 inline-flex items-center gap-4 text-xs text-[#273E47]/65">
                  <span className="inline-flex items-center gap-1">
                    <MessageCircle size={12} /> {thread.commentCount}
                  </span>
                </p>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
